import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, { buildFaqSchema, buildLocalBusinessSchema } from "@/components/SeoLandingPage";
import { buildBreadcrumbSchema, ldScript, pageHead, SITE_URL } from "@/lib/page-schema";
import heroImage from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-fc-exec.jpg?format=webp&quality=72&w=1400&imagetools";
import execImg from "@/assets/uga-game-day-executive-sprinter-interior.jpg?format=webp&quality=72&w=1400&imagetools";
import jetImg from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1400&imagetools";
import limoImg from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";

const PATH = "/sprinter-van-service-atlanta";
const TITLE = "Sprinter Rental With Driver in Atlanta | Chauffeured Mercedes Sprinters";
const DESC =
  "Rent a Mercedes-Benz Sprinter with a professional chauffeur in Atlanta. Executive, Jet and Limo Style Sprinters for groups, airport arrivals and game days. From $250/hour. Call (678) 431-7222.";

const faqs = [
  {
    q: "What is the difference between your Sprinter service and your Sprinter limo?",
    a: "This page covers group and executive travel: corporate groups, airport arrivals, game days. If you are planning a night out or a celebration, our Sprinter Limo page covers the Limo Style Sprinter experience.",
  },
  {
    q: "Which Sprinter should I choose?",
    a: "Executive Style for working groups and client travel, the Jet Sprinter for a smaller group that wants a private-jet style cabin, and the Limo Style Sprinter when the ride is part of the celebration.",
  },
  {
    q: "Does a chauffeur come with every Sprinter?",
    a: "Yes. Every booking comes with one of our own long-tenured chauffeurs, background-checked and fingerprinted. We own every vehicle in our fleet.",
  },
];

export const Route = createFileRoute("/sprinter-van-service-atlanta")({
  head: () =>
    pageHead({
      path: PATH,
      title: TITLE,
      description: DESC,
      scripts: [
        ldScript(
          buildLocalBusinessSchema({
            url: `${SITE_URL}${PATH}`,
            name: "Atlanta Luxury Sprinters, Sprinter Service Atlanta",
            image: `${SITE_URL}/favicon.ico`,
            description: DESC,
            serviceType: "Chauffeured Sprinter Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Alpharetta" },
            ],
          }),
        ),
        ldScript(buildFaqSchema(faqs)),
        ldScript(
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Sprinter Van Service Atlanta", path: PATH },
          ]),
        ),
      ],
    }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Chauffeured Sprinters • Atlanta"
      h1="Sprinter Van Service in Atlanta"
      intro="Your group needs to travel together. I'll help you choose a chauffeured Mercedes-Benz Sprinter for meetings, airport arrivals or game day, with your vehicle and chauffeur confirmed before pickup."
      hideQuotePricing
      heroImage={heroImage}
      heroAlt="Executive Style Mercedes-Benz Sprinter cabin for group and executive travel in Atlanta"
      midImage={midImage}
      midAlt="Executive captain seating inside a chauffeured Mercedes-Benz Sprinter"
      sectionOneTitle="One Group, One Plan"
      sectionOneBody={[
        "When a group needs to move together, a chauffeured Mercedes-Benz Sprinter keeps everyone in one cabin, on one schedule, with one point of contact. It's how executive teams, wedding families and game day groups travel across Atlanta without a convoy of cars.",
        "We own every vehicle in our fleet. Our Sprinters are 2025 or newer, custom-built and detailed before and after every job, and our chauffeurs are background-checked, fingerprinted and long-tenured with us. You'll know your vehicle and your chauffeur before pickup, and you can track your chauffeur live in our app.",
        "Planning a night out or a celebration instead? See our Sprinter Limo Atlanta page for the Limo Style Sprinter experience.",
      ]}
      bulletsTitle="What We Use Sprinters For"
      bulletsIntro="Tell me which kind of trip you're planning."
      bullets={[
        "Corporate group transfers between hotels, offices and venues",
        "Executive and client travel across the metro",
        "Airport group transfers at Hartsfield-Jackson and private airports",
        "Game days in Athens and at Mercedes-Benz Stadium",
        "Wedding family and guest movement",
      ]}
      coverageTitle="Where Our Sprinters Run"
      coverageIntro="In town and out of the perimeter, wherever your group needs to be."
      coverage={["Buckhead", "Midtown", "Downtown Atlanta", "Hartsfield-Jackson (ATL)", "PDK, FTY and RYY", "Alpharetta", "Sandy Springs", "Athens", "Mercedes-Benz Stadium"]}
      compareTable={{
        title: "Executive Style vs. Jet Sprinter vs. Limo Style",
        intro: "Three Sprinters, three different jobs. Choose by what the ride is for.",
        headers: ["Sprinter", "Best for"],
        rows: [
          ["Executive Style Sprinter", "Working groups, client travel, corporate group transfers and airport arrivals. A calm, professional cabin."],
          ["Jet Sprinter", "Smaller groups and VIP travel that want a private-jet style cabin with captain seats."],
          ["Limo Style Sprinter", "When the ride is part of the plan: game days, celebrations and nights out, with bar and lounge seating."],
        ],
      }}
      gallery={{
        title: "Choose Your Sprinter",
        images: [
          { src: execImg, alt: "Executive Style Sprinter interior", caption: "Executive Style Sprinter" },
          { src: jetImg, alt: "Jet Sprinter with white leather captain seats", caption: "Jet Sprinter" },
          { src: limoImg, alt: "Limo Style Sprinter lounge with bar", caption: "Limo Style Sprinter" },
        ],
      }}
      faqs={faqs}
      related={[
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "Nights out and celebrations in the Limo Style Sprinter." },
        { to: "/corporate-transportation-atlanta", title: "Corporate Transportation", desc: "Dedicated accounts for executive teams." },
        { to: "/atlanta-airport-car-service", title: "Atlanta Airport Car Service", desc: "Group and executive transfers at every Atlanta airport." },
      ]}
      ctaTitle="Text Me Your Group's Plans"
      ctaBody="Send me your date, group size and itinerary. I'll help you choose the Sprinter, check availability myself and send your quote in writing."
    />
  );
}
